import { FC, FunctionComponent, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import {
    ErrorBoundary,
    PermissionRoute,
    PermissionRouteProps,
    ErrorPage,
    ProtectedRoute,
    CookieBanner,
    MainLayout,
    Login,
    ResetPassword,
    ForgottenPassword,
    Permission,
    useLogs,
    useAuth,
} from '@isocel/front-common';

import './assets/styles/App.less';
import { getRawRoute, getRoute, RoutePathName } from './routes';
import Home from './pages/home';
import { getAllPackagingFlow } from './queries/api/packaging-flow';
import { AppUser, PackagingFlowDirection, RoleSlug, StoreUser } from './queries/api/types';
/* import { Dashboard } from './pages/dashboard'; */
import ShipmentTrack from './pages/shipment/ShipmentTrack';
import { ShipmentSuccess, Support } from './pages/shipment';
import ShipmentType from './pages/shipmentType';
import BeforeLoading from './pages/beforeLoading';
import AfterLoading from './pages/afterLoading/AfterLoading';
import DeliveryInfo from './pages/receptionStore/deliveryInformation/DeliveryInfo';
import { ReceptionDetails, ReceptionInfo, ReceptionView } from './pages/receptionStore';
import Registration from './pages/receptionStore/registration/Registration';
import Dashboard from './pages/dashboard/Dashboard';
import { PackagingRecipient, RecipientMovement, RecipientTrack } from './pages/packingMovement';
import ReceptionStore from './pages/receptionStore/receptionStore/ReceptionStore';
import ReceptionFromSupplier from './pages/receptionStore/receptionFromSupplier/ReceptionFromSupplier';
import ReceptionComment from './pages/packingMovement/ReceptionComment';
import { PalettCreate } from './pages/palettManagement';
import { Qrparc, QrReading } from './pages/qrReading';
import QrPackageTrack from './pages/qrReading/QrPackageTrack';
import QrPackageUpdate from './pages/qrReading/QrPackageUpdate';
import QrCameraPage from './pages/qrReading/QrCamera';
import ReceptionsAValider from './pages/receptionsAValider/ReceptionsAValider';
import LitigesAValider from './pages/litigesAValider/LitigesAValider';
import Counter from './pages/counters/counter';
import InventoryHistory from './pages/inventoryHistory/InventoryHistory';
import GestionMagasins from './pages/gestionMagasins/GestionMagasins';
import FacturationManuelle from './pages/facturationManuelle/FacturationManuelle';
import HistoriqueFacturation from './pages/historiqueFacturation/HistoriqueFacturation';
import { getSelectedKey, menuItems, canAccessMouvementsDivers, canAccessReceptionsAValider } from './config/menu-items';
import ScaUsers from './pages/scaUsers';
import StoreUsers from './pages/storeUsers';
import ChangePassword from './pages/changePassword/changePassword';

const routes: Array<PermissionRouteProps & { Children: FunctionComponent }> = [
    {
        path: getRawRoute(RoutePathName.home),
        Children: Home,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.dashboard),
        Children: Dashboard,
    },
    {
        path: getRawRoute(RoutePathName.shipmentType),
        Children: ShipmentType,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionComment),
        Children: ReceptionComment,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionDetails),
        Children: ReceptionDetails,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionView),
        Children: ReceptionView,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.successShipment),
        Children: ShipmentSuccess,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionView),
        Children: ReceptionView,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.recipient),
        permission: Permission.storeUser,
        Children: PackagingRecipient,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.recipientMovement),
        Children: RecipientMovement,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.recipientTrack),
        Children: RecipientTrack,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionInfo),
        permission: Permission.orgUser,
        Children: ReceptionInfo,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.supportTrack),
        Children: Support,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.shipmentTrack),
        Children: ShipmentTrack,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionStore),
        permission: Permission.storeUser,
        Children: ReceptionStore,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.deliveryInfo),
        Children: DeliveryInfo,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionFromSupplier),
        Children: ReceptionFromSupplier,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.beforeLoading),
        permission: Permission.orgUser,
        Children: BeforeLoading,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.afterLoading),
        permission: Permission.orgUser,
        Children: AfterLoading,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.registration),
        Children: Registration,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.palettCreate),
        Children: PalettCreate,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.qrReading),
        Children: QrReading,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.qrParcs),
        permission: Permission.orgUser,
        Children: Qrparc,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.qrPackageTrack),
        Children: QrPackageTrack,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.qrPackageUpdate),
        Children: QrPackageUpdate,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.qrCamera),
        Children: QrCameraPage,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.receptionsAValider),
        Children: ReceptionsAValider,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.scaUsers),
        permission: Permission.orgAdmin,
        Children: ScaUsers,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.storeUsers),
        permission: Permission.orgAdmin,
        Children: StoreUsers,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.changePassword),
        Children: ChangePassword,
    },
    {
        path: getRawRoute(RoutePathName.counters),
        Children: Counter,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.inventoryHistory),
        permission: Permission.dashboard,
        Children: InventoryHistory,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.gestionMagasins),
        permission: Permission.orgAdmin,
        Children: GestionMagasins,
    },
    {
        path: getRawRoute(RoutePathName.litigesAValider),
        permission: Permission.orgAdmin,
        Children: LitigesAValider,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.facturationManuelle),
        permission: Permission.orgAdmin,
        Children: FacturationManuelle,
        exact: true,
    },
    {
        path: getRawRoute(RoutePathName.historiqueFacturation),
        permission: Permission.orgAdmin,
        Children: HistoriqueFacturation,
        exact: true,
    },
];

const App: FC = () => {
    const { sendLogs } = useLogs();
    const { pathname } = useLocation();
    const isExportRoute = pathname.startsWith('/exports/');
    const [menuData, setMenuData] = useState([]);
    const [receptionsCount, setReceptionsCount] = useState(0);
    const [litigesCount, setLitigesCount] = useState(0);
    const { user } = useAuth();
    const mustChangePassword = (user as AppUser | undefined)?.needPasswordChange === true;
    const changePasswordPath = getRawRoute(RoutePathName.changePassword);

    useEffect(() => {
        if (mustChangePassword) {
            return;
        }
        const isStoreUser = user?.role?.slug === RoleSlug.storeAdmin || user?.role?.slug === RoleSlug.storeUser;
        const isOrgUser = user?.role?.slug === RoleSlug.orgAdmin || user?.role?.slug === RoleSlug.orgUser;
        const isOrgAdmin = user?.role?.slug === RoleSlug.orgAdmin;
        const storeId = (user as StoreUser)?.store;

        if (isStoreUser && storeId) {
            getAllPackagingFlow(
                {
                    page: 1,
                    pageSize: 1,
                    flowType: PackagingFlowDirection.ShippedToStore,
                    storeIds: [storeId],
                    received: false,
                },
                { organization: { id: user?.organization?.id ?? '' } }
            )
                .then((response) => setReceptionsCount(response.totalCount))
                .catch(() => undefined);
        } else if (isOrgUser) {
            getAllPackagingFlow(
                {
                    page: 1,
                    pageSize: 1,
                    flowType: PackagingFlowDirection.ShippedToOrganization,
                    received: false,
                },
                { organization: { id: user?.organization?.id ?? '' } }
            )
                .then((response) => setReceptionsCount(response.totalCount))
                .catch(() => undefined);
        }

        if (isOrgAdmin) {
            getAllPackagingFlow(
                {
                    page: 1,
                    pageSize: 1,
                    flowType: PackagingFlowDirection.RecivedInStore,
                    litigeStatus: ['Non vérifié'],
                },
                { organization: { id: user?.organization?.id ?? '' } }
            )
                .then((response) => setLitigesCount(response.totalCount))
                .catch(() => undefined);
        } else {
            setLitigesCount(0);
        }
    }, [user, mustChangePassword]);

    useEffect(() => {
        if (mustChangePassword) {
            setMenuData([]);
            return;
        }
        // Filter menu items based on user permissions
        const filteredMenuItems = menuItems(receptionsCount, litigesCount, user ?? null).filter((item) => {
            // Special handling for "Mouvements divers"
            if (item.key === '/palettCreate') {
                return canAccessMouvementsDivers(user ?? null);
            }
            // Special handling for "Réceptions à valider"
            if (item.key === getRoute(RoutePathName.receptionsAValider)) {
                return canAccessReceptionsAValider(user ?? null);
            }
            return true;
        });
        setMenuData(filteredMenuItems as any);
    }, [user, receptionsCount, litigesCount, mustChangePassword]);

    if (mustChangePassword && pathname !== changePasswordPath) {
        return <Redirect to={changePasswordPath} />;
    }

    const pageContent = (
        <ErrorBoundary sendLogs={sendLogs}>
            <Switch>
                {routes.map(({ Children, ...props }) => {
                    const isReceptionsRoute = props.path === getRawRoute(RoutePathName.receptionsAValider);
                    return (
                        <PermissionRoute
                            key={Array.isArray(props.path) ? props.path.join('') : props.path?.toString()}
                            {...props}
                        >
                            {isReceptionsRoute ? (
                                <ReceptionsAValider onCountChange={setReceptionsCount} />
                            ) : (
                                <Children />
                            )}
                        </PermissionRoute>
                    );
                })}

                <Route path="*">
                    <ErrorPage />
                </Route>
            </Switch>
        </ErrorBoundary>
    );
    return (
        <Switch>
            <Route exact path={getRawRoute(RoutePathName.login)} component={Login} />
            <Route exact path={getRawRoute(RoutePathName.forgottenPassword)} component={ForgottenPassword} />
            <Route exact path={getRawRoute(RoutePathName.resetPassword)} component={ResetPassword} />

            <ProtectedRoute>
                {isExportRoute ? (
                    <div id="export-wrapper">{pageContent}</div>
                ) : (
                    <div className={mustChangePassword ? 'password-change-required' : undefined}>
                        <CookieBanner />
                        <MainLayout menuItems={menuData} getSelectedKey={getSelectedKey}>
                            {pageContent}
                        </MainLayout>
                    </div>
                )}
            </ProtectedRoute>
        </Switch>
    );
};

export default App;
